import React, { useEffect, useRef, useState } from 'react';
// eslint-disable-next-line import/no-unresolved
import { ActivityIndicator } from 'LocalGluons';
import { Animated, PanResponder, Platform, Text, View } from 'react-native';
import { useStore } from 'Store';
import type { PlayerData } from 'Types';
import {
    lockToPortrait,
    lockToLandscape,
    getActualWidth,
    hideBars,
    showBars,
    Device
} from 'DeviceUtils';
import Player from '../Player';
import LoadingContainer from './components/LoadingContainer';
import useStreamData from './hooks/useStreamData';
import styles from './styles';
import useConnection from './hooks/useConnection';
import NoNetworkDialog from './components/NoNetworkDialog';
import NetworkWarningDialog from './components/NetworkWarningDialog';
import ContinueWatchingDialog from './components/ContinueWatchingDialog';
import Background from '../Background';
import { ROTATION_WAIT_DURATION } from '../../../options';
import { ThumbnailsProvider } from './hooks/useThumbnails';

interface PlayerContainerProps {
    playerData: PlayerData;
}

enum WaitingStatus {
    INIT = 'INIT',
    WAITING = 'WAITING',
    DONE = 'erDONEror',
}

const PlayerContainer = ({ playerData }: PlayerContainerProps): JSX.Element => {
    const {
        getStreamData,
        itemStreamData,
        isLoading,
        continueWatchingData,
        onContinueWatching,
        onRestart,
        modalDisplayed
    } = useStreamData(playerData);
    const { setPlayerData } = useStore();
    const [isMiniPlayer, setMiniPlayer] = useState(false);
    const [rotationWait, setRotationWait] = useState<WaitingStatus>(WaitingStatus.INIT);
    const [rotationWaitTimeout, setRotationWaitTimeout] =
        useState<ReturnType<typeof setTimeout> | null>(null);
    const [compressPlayer, setCompressPlayer] = useState(false);
    const {
        showCellularWarning,
        connected,
        discardCellularWarning,
        retryConnection,
        dontShowAgainPress
    } = useConnection();

    const translateX = useRef(new Animated.Value(0));

    const onVideoPress = () => {
        lockToLandscape();
        setMiniPlayer(false);
        hideBars();
    };

    const onClose = () => {
        if (!isMiniPlayer) {
            setPlayerData(null);

            return;
        }
        setCompressPlayer(true);
        setTimeout(() => {
            setPlayerData(null);
        }, 150);
    };

    const panResponder = useRef(
        PanResponder.create({
            onStartShouldSetPanResponder: () => true,
            onStartShouldSetPanResponderCapture: () => false,
            // onMoveShouldSetResponderCapture: () => true,
            onMoveShouldSetPanResponder: (evt, gestureState) => {
                const { dx, dy } = gestureState;
                return dx > 2 || dx < -2 || dy > 2 || dy < -2;
            },
            onMoveShouldSetPanResponderCapture: (evt, gestureState) =>
                gestureState.dx !== 0 && gestureState.dy !== 0,
            onPanResponderMove: Animated.event([null, { dx: translateX.current }], {
                useNativeDriver: true
            }),
            onPanResponderTerminationRequest: () => true,
            onPanResponderRelease: (event, { dx }) => {
                const screenWidth = getActualWidth();

                if (Math.abs(dx) >= 0.5 * screenWidth) {
                    Animated.timing(translateX.current, {
                        toValue: dx > 0 ? screenWidth : -screenWidth,
                        duration: 200,
                        useNativeDriver: true
                    }).start(onClose);
                } else if (Math.abs(dx) <= 5) {
                    onVideoPress();
                } else {
                    Animated.spring(translateX.current, {
                        toValue: 0,
                        bounciness: 2,
                        useNativeDriver: true
                    }).start();
                }
            }
        })
    ).current;

    useEffect(() => {
        hideBars();
        return () => {
            if (!Device.isTabletOrWeb) lockToPortrait();
            showBars();
            if (rotationWaitTimeout) clearTimeout(rotationWaitTimeout);
        };
    }, []);

    useEffect(() => {
        if (!isLoading && itemStreamData && rotationWait === WaitingStatus.INIT) {
            setRotationWait(WaitingStatus.WAITING);
            lockToLandscape();
            const timeout = setTimeout(() => {
                setRotationWait(WaitingStatus.DONE);
            }, ROTATION_WAIT_DURATION);
            setRotationWaitTimeout(timeout);
        }
    }, [isLoading, itemStreamData, rotationWait]);

    const onMiniPlayerPress = () => {
        if (!Device.isTabletOrWeb) lockToPortrait();
        showBars();
        setMiniPlayer(true);
    };

    // useEffect(() => {

    //     // if (Platform.OS === 'android') {
    //     //     const data = checkScreenCapture();
    //     //     console.log('AHOOOOJ');

    //     //     console.log('checkScreenCapture', data, checkScreenCapture);
    //     // }

    //     const fetchData = async () => {
    //         if (Platform.OS === 'android') {
    //             const data = await checkScreenCapture();
    //             console.log('AHOOOOJ');
    //             console.log('Navratova hodnota', data);
    //         }
    //     };

    //     fetchData();
    // }, [checkScreenCapture]);


    // To hide UI under transparent modal
    if (modalDisplayed)
        return (
            <Background style={styles.container} customImage={playerData?.item?.getImageUrl()} />
        );

    if (!itemStreamData || rotationWait !== WaitingStatus.DONE) {
        return (
            <LoadingContainer
                onBack={onClose}
                showHeader={rotationWait !== WaitingStatus.WAITING}>
                {rotationWait === WaitingStatus.INIT && <ActivityIndicator opaque isOverlay />}
            </LoadingContainer>
        );
    }

    if (!connected) {
        return (
            <NoNetworkDialog
                item={playerData.item}
                onClose={onClose}
                onBack={onClose}
                onRetry={retryConnection}
            />
        );
    }

    if (showCellularWarning && !isLoading) {
        return (
            <NetworkWarningDialog
                item={playerData.item}
                onClose={onClose}
                onBack={onClose}
                onPlay={discardCellularWarning}
                onCheckboxPress={dontShowAgainPress}
            />
        );
    }

    // if (continueWatchingData && playerData.item.modelType !== modelType.tv) {
    if (continueWatchingData && !isLoading) {
        return (
            <ContinueWatchingDialog
                item={playerData.item}
                continueWatchingData={continueWatchingData}
                onBack={onClose}
                onContinue={onContinueWatching}
                onRestart={onRestart}
            />
        );
    }

    return (
        <Animated.View
            style={[
                !isMiniPlayer ? styles.container : styles.miniContainer,
                isMiniPlayer ? { transform: [{ translateX: translateX.current }] } : {},
                // This will prevent flicker when orientation change is used after back
                compressPlayer ? { width: 0, height: 0 } : {},
            ]}
            // This is the react way of handling it
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...(isMiniPlayer ? panResponder.panHandlers : {})}
            testID={isMiniPlayer ? 'miniplayer' : 'player'}
        >
            <ThumbnailsProvider>
                <Player
                    itemStreamData={itemStreamData}
                    playerData={playerData}
                    onClose={onClose}
                    isMiniPlayer={isMiniPlayer}
                    onMiniPlayerPress={onMiniPlayerPress}
                    getStreamData={getStreamData}
                />
            </ThumbnailsProvider>
        </Animated.View >
    );
};

export default PlayerContainer;
